import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { postDataAPI } from '../../utils/fetchData';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { logoutThunk } from '../../store/auth/authThunk';
import LayoutHeader from '../../layout/LayoutHeader';
import RedirectCard from '../../components/More/RedirectCard';
import CrossArrow from '../../svg/CrossArrow';

const TeamMemberDashboard = () => {
  const [managedArtists, setManagedArtists] = useState([]);
  const [invitationRequests, setInvitationRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [respondingToInvite, setRespondingToInvite] = useState(null);
  const [leavingTeam, setLeavingTeam] = useState(null);
  const { userArticle } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    fetchManagedArtists();
    fetchInvitationRequests();
  }, []);

  // Re-fetch data when returning from delegated mode
  useEffect(() => {
    const handleStorageChange = () => {
      // If selectedArtist was cleared (returned to team dashboard), re-fetch
      const selectedArtist = localStorage.getItem('selectedArtist');
      if (!selectedArtist) {
        console.log('🔄 Returned to team dashboard, re-fetching managed artists');
        fetchManagedArtists();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for manual navigation changes
    const interval = setInterval(() => {
      const currentPath = window.location.pathname;
      const selectedArtist = localStorage.getItem('selectedArtist');
      
      if (currentPath === '/team-member-dashboard' && !selectedArtist && managedArtists.length === 0) {
        console.log('🔄 On team dashboard without data, re-fetching');
        fetchManagedArtists();
      }
    }, 30000); // Changed from 1 second to 30 seconds to prevent excessive API calls

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [managedArtists.length]);

  const fetchManagedArtists = async () => {
    // Prevent multiple simultaneous calls
    if (loading) {
      console.log('⏸️ Already fetching managed artists, skipping...');
      return;
    }
    
    setLoading(true);
    
    // ALWAYS use localStorage for team member operations - never trust Redux userArticle
    // Redux userArticle gets contaminated with artist data during delegated access
    const localStorageUser = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
    
    console.log('🔍 Full userData object (Redux):', userArticle);
    console.log('🔍 LocalStorage user:', localStorageUser);
    console.log('🔍 FORCING use of localStorage user for team operations');
    console.log('🔍 Fetching managed artists for email:', localStorageUser?.email);
    console.log('🔍 Email exists?', !!localStorageUser?.email);
    
    if (!localStorageUser?.email) {
      console.error('❌ No email found in localStorage user data!');
      dispatch(setErrorAlert('User email not found. Please refresh and try again.'));
      setLoading(false);
      return;
    }
    
    try {
      const requestData = {
        teamMemberEmail: localStorageUser?.email
      };
      console.log('🔍 Request data being sent:', requestData);
      console.log('🔍 Team member email being sent:', localStorageUser?.email);
      console.log('🔍 Full effective user object:', localStorageUser);
      
      const token = localStorage.getItem('dizeeToken');
      console.log('🔍 Token available for managed artists:', !!token);
      const response = await postDataAPI('team/managed-artists', requestData, token);

      console.log('📡 Response from managed-artists:', response);
      console.log('📡 Response status:', response.status);
      console.log('📡 Response data:', response.data);
      console.log('📡 Response data.data:', response.data?.data);
      console.log('📡 Response data.data length:', response.data?.data?.length);

      if (response.data?.status === 200) {
        console.log('✅ Managed artists loaded:', response.data.data);
        console.log('✅ Setting managedArtists to:', response.data.data || []);
        setManagedArtists(response.data.data || []);
      } else {
        console.error('❌ Failed response:', response.data);
        dispatch(setErrorAlert(response.data?.message || 'Failed to load managed artists'));
      }
    } catch (error) {
      console.error('💥 Error fetching managed artists:', error);
      console.error('💥 Error response data:', error.response?.data);
      console.error('💥 Error status:', error.response?.status);
      dispatch(setErrorAlert('Failed to load managed artists'));
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitationRequests = async () => {
    setLoadingRequests(true);
    
    // Use localStorage for team member operations
    const localStorageUser = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
    
    console.log('🔔 Fetching invitation requests for:', localStorageUser?.email);
    
    if (!localStorageUser?.email) {
      console.error('❌ No email found for fetching invitations');
      setLoadingRequests(false);
      return;
    }

    try {
      const token = localStorage.getItem('dizeeToken');
      console.log('🔔 Token available for invitation requests:', !!token);
      const response = await postDataAPI('team/invitation-requests', {
        email: localStorageUser?.email
      }, token);

      console.log('📬 Invitation requests response:', response);

      if (response.data?.status === 200) {
        console.log('✅ Invitation requests loaded:', response.data.data);
        setInvitationRequests(response.data.data || []);
      } else {
        console.error('❌ Failed to load invitation requests:', response.data);
        dispatch(setErrorAlert(response.data?.message || 'Failed to load invitation requests'));
      }
    } catch (error) {
      console.error('💥 Error fetching invitation requests:', error);
      dispatch(setErrorAlert('Failed to load invitation requests'));
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSwitchToArtist = (artist) => {
    console.log('🎯 Switching to artist:', artist);
    // Store the selected artist and redirect to their main dashboard (acting as the artist)
    localStorage.setItem('selectedArtist', JSON.stringify(artist));
    console.log('🎯 Artist stored in localStorage, navigating to /');
    navigate('/');
  };

  const handleAcceptInvitation = async (invitation) => {
    setRespondingToInvite(invitation._id);
    
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/respond-invitation', {
        invitationId: invitation._id
      }, token);

      if (response.data?.status === 200) {
        const { requiresBilling, accountType, teamMemberId } = response.data.data;
        
        // If Manager account, redirect to billing
        if (requiresBilling && accountType === 'manager') {
          dispatch(setSuccessAlert('Invitation accepted! Please complete billing to activate your Manager account.'));
          
          // Create billing checkout session
          const billingResponse = await postDataAPI('team/create-manager-subscription', {
            teamMemberId: teamMemberId,
            billingCycle: 'monthly' // Default to monthly, can be changed later
          }, token);
          
          if (billingResponse.data?.status === 200 && billingResponse.data.data?.checkoutUrl) {
            // Redirect to Stripe checkout
            window.location.href = billingResponse.data.data.checkoutUrl;
          } else {
            dispatch(setErrorAlert('Billing setup failed. Please contact support.'));
          }
        } else {
          // Support account or already active - no billing needed
          dispatch(setSuccessAlert(`Successfully joined ${invitation.artistName}'s team!`));
          // Refresh both lists
          fetchInvitationRequests();
          fetchManagedArtists();
        }
      } else {
        dispatch(setErrorAlert(response.data?.message || 'Failed to accept invitation'));
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      dispatch(setErrorAlert('Failed to accept invitation'));
    } finally {
      setRespondingToInvite(null);
    }
  };

  const handleDeclineInvitation = async (invitation) => {
    setRespondingToInvite(invitation._id);
    
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/decline-invitation', {
        invitationId: invitation._id
      }, token);

      if (response.data?.status === 200) {
        dispatch(setSuccessAlert('Invitation declined'));
        fetchInvitationRequests();
      } else {
        dispatch(setErrorAlert(response.data?.message || 'Failed to decline invitation'));
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      dispatch(setErrorAlert('Failed to decline invitation'));
    } finally {
      setRespondingToInvite(null);
    }
  };

  const handleLeaveTeam = async (artist) => {
    if (!window.confirm(`Are you sure you want to leave ${artist.name}'s team? This action cannot be undone.`)) {
      return;
    }

    setLeavingTeam(artist._id);
    
    // Use localStorage for team member operations
    const localStorageUser = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
    
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/leave-team', {
        artistId: artist._id,
        teamMemberEmail: localStorageUser?.email
      }, token);

      if (response.data?.status === 200) {
        dispatch(setSuccessAlert(`Successfully left ${artist.name}'s team`));
        // Refresh the managed artists list
        fetchManagedArtists();
      } else {
        dispatch(setErrorAlert(response.data?.message || 'Failed to leave team'));
      }
    } catch (error) {
      console.error('Error leaving team:', error);
      dispatch(setErrorAlert('Failed to leave team'));
    } finally {
      setLeavingTeam(null);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getUserRoleDisplayName = () => {
    const roleNames = {
      'manager': 'Manager',
      'agent': 'Agent', 
      'label': 'Label',
      'publisher': 'Publisher',
      'business-manager': 'Business Manager'
    };
    
    return roleNames[userArticle?.professionRole] || 'Team Member';
  };

  return (
    <LayoutHeader>
      <div className="w-[390px] flex flex-col justify-start items-center relative z-[0]">
        <div className="px-4 my-[50px] flex w-full justify-between">
          <p className="text-white">{getUserRoleDisplayName()} Dashboard</p>
          <div className='flex gap-[20px]'>
            <p onClick={() => dispatch(logoutThunk())} className='text-white cursor-pointer'>Logout</p>
          </div>
        </div>

        <div className="w-full px-4">

          {/* Invitation Requests Section */}
          {invitationRequests.length > 0 && (
            <div className="mb-6">
              <p className="text-white text-[12px] font-semibold mb-4 text-left">
                Team Invitations ({invitationRequests.length})
              </p>
              
              {invitationRequests.map((invitation) => (
                <div key={invitation._id} className="py-4 flex justify-between items-center border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-bold">
                      {invitation.artistAvatar ? (
                        <img 
                          src={invitation.artistAvatar} 
                          alt={invitation.artistName}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(invitation.artistName)
                      )}
                    </div>
                    <div>
                      <p className="text-white text-[12px] font-medium">{invitation.artistName}</p>
                      <p className="text-white/60 text-[10px]">wants you as {invitation.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAcceptInvitation(invitation)}
                      disabled={respondingToInvite === invitation._id}
                      className="text-green-400 text-[10px] px-2 py-1 rounded hover:bg-green-400/10 transition-colors"
                    >
                      {respondingToInvite === invitation._id ? 'Accepting...' : 'Accept'}
                    </button>
                    <button
                      onClick={() => handleDeclineInvitation(invitation)}
                      disabled={respondingToInvite === invitation._id}
                      className="text-red-400 text-[10px] px-2 py-1 rounded hover:bg-red-400/10 transition-colors"
                    >
                      {respondingToInvite === invitation._id ? 'Declining...' : 'Decline'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Your Artists Section */}
          {managedArtists.length > 0 ? (
            <div className="mb-6">
              <p className="text-white text-[12px] font-semibold mb-4 text-left">
                Your Artists ({managedArtists.length})
              </p>
              
              {managedArtists.map((artist) => (
                <div key={artist._id} className="py-4 flex justify-between items-center border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-bold">
                    {artist.profilePicture ? (
                      <img 
                        src={artist.profilePicture} 
                        alt={artist.name}
                          className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      getInitials(artist.name)
                    )}
                    </div>
                    <div>
                      <p className="text-white text-[12px] font-medium">{artist.name}</p>
                      <p className="text-white/60 text-[10px]">As {artist.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSwitchToArtist(artist)}
                      className="text-blue-400 text-[10px] px-2 py-1 rounded hover:bg-blue-400/10 transition-colors"
                    >
                      Enter
                    </button>
                    <button
                      onClick={() => handleLeaveTeam(artist)}
                      disabled={leavingTeam === artist._id}
                      className="text-red-400 text-[10px] px-2 py-1 rounded hover:bg-red-400/10 transition-colors"
                    >
                      {leavingTeam === artist._id ? 'Leaving...' : 'Leave'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mb-6">
              <p className="text-white text-[12px] font-semibold mb-4 text-left">
                Your Artists (0)
              </p>
              <div className="text-white/60 text-[12px] text-center py-8">
                {loading ? (
                  <div>Loading your teams...</div>
                ) : (
                  <div>
                    <p>No teams found.</p>
                    <p className="text-[10px] mt-2 opacity-60">
                      Make sure you've been invited to a team and completed signup.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Settings Options */}
          <div className="mb-6">
            <p className="text-white text-[12px] font-semibold mb-4 text-left">Settings</p>
            
            <div className="w-full py-6 flex justify-between items-center" onClick={() => navigate('/team-member/profile')}>
              <span className="text-[12px] text-white cursor-pointer">Profile</span>
              <div className="cursor-pointer text-white mr-4">
                <CrossArrow className="h-[14px] w-[14px]" />
              </div>
            </div>
            
            <div className="w-full py-6 flex justify-between items-center" onClick={() => navigate('/team-member/subscription')}>
              <span className="text-[12px] text-white cursor-pointer">Subscription</span>
              <div className="cursor-pointer text-white mr-4">
                <CrossArrow className="h-[14px] w-[14px]" />
              </div>
            </div>
            
            <div className="w-full py-6 flex justify-between items-center" onClick={() => navigate('/team-member/advanced')}>
              <span className="text-[12px] text-white cursor-pointer">Advanced</span>
              <div className="cursor-pointer text-white mr-4">
                <CrossArrow className="h-[14px] w-[14px]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default TeamMemberDashboard; 